import { useState, useCallback, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToastContext } from '@resideo/blueprint-oneportal';
import type { UpdateUserInviteDataModel } from '@resideo/web-integration-services-api-client';
import apiService, { DeviceFilter } from '@/services/apiService';
import type {
  ConsumerLocationDetail,
  ConsumerDeviceItem,
  ConsumerUserWithLocations,
} from '@/services/apiService';
import { StepLocation } from './InviteEmployeeStepLocation';
import type { LocationRow } from './InviteEmployeeStepLocation';
import styles from './AddLocationPage.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

type InviteRole = NonNullable<UpdateUserInviteDataModel['inviteDataInfo']>['role'];
type ScheduleInfo = NonNullable<NonNullable<UpdateUserInviteDataModel['inviteDataInfo']>['scheduleInfo']>;
type WeekDays = ScheduleInfo['weekDays'];

const ALL_WEEK_DAYS = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
] as unknown as WeekDays;

export interface AddLocationHandle {
  save: () => void;
}

export interface AddLocationPageProps {
  consumerAccountId: string;
  consumerUserId: string;
  email: string;
  role: string;
  invitedUserId: string | null;
  onSavingChange?: (saving: boolean) => void;
  onValidChange?: (valid: boolean) => void;
  onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function filterByFamily(devices: ConsumerDeviceItem[], family: string) {
  return devices
    .filter(d => d.device?.product?.productFamily === family)
    .map(d => ({ id: d.device?.deviceId ?? d.id ?? '', name: d.name ?? null }));
}

// ─── Component ────────────────────────────────────────────────────────────────

export const AddLocationPage = forwardRef<AddLocationHandle, AddLocationPageProps>(
  function AddLocationPage({
    consumerAccountId,
    consumerUserId,
    email,
    role,
    invitedUserId,
    onSavingChange,
    onValidChange,
    onClose,
  }, ref) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { addToast } = useToastContext();

  // ── API 1: user's currently assigned locations + devices ──────────────────
  // Reuses React Query cache populated by the Locations tab — no extra request.
  const { data: detailData } = useQuery({
    queryKey: ['consumerUserDetail', consumerAccountId, consumerUserId],
    queryFn: () => apiService.getConsumerUserDetail(consumerAccountId, consumerUserId),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  // ── API 2: full account device inventory ──────────────────────────────────
  // Reuses cache from invite flow if warm.
  const { data: accountsData, isFetching: isLoadingLocations, error: accountsError } = useQuery({
    queryKey: ['consumerAccounts', 'ALL'],
    queryFn: () => apiService.getConsumerAccounts(undefined, DeviceFilter.ALL),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  // ── Already-assigned location IDs (to filter them out) ────────────────────
  const assignedLocationIds = useMemo<Set<string>>(() => {
    const locs: ConsumerLocationDetail[] = detailData?.data?.consumerAccount?.locations ?? [];
    return new Set(
      locs
        .filter(l => (l.consumerDevices ?? []).length > 0)
        .map(l => l.id)
        .filter((id): id is string => Boolean(id)),
    );
  }, [detailData]);

  // ── Build LocationRow[] from unassigned locations only ────────────────────
  const availableLocations = useMemo<LocationRow[]>(() => {
    const allConsumerUsers: ConsumerUserWithLocations[] = accountsData?.data?.consumerUsers ?? [];
    // Only include locations that belong to the current consumerAccountId so that
    // locations from other accounts are excluded from the selection list.
    const rawLocations: ConsumerLocationDetail[] = allConsumerUsers
      .filter(cu => cu?.consumerAccount?.id === consumerAccountId)
      .flatMap(cu => cu?.consumerAccount?.locations ?? []);
    return rawLocations
      .filter(loc => !assignedLocationIds.has(loc.id ?? ''))
      .map(loc => {
        const addr = loc.address ?? {};
        const parts = [
          addr.addressLine1,
          addr.city,
          addr.stateProvinceRegionCode,
          addr.zipPostalCode,
        ].filter(Boolean);
        const allDevices: ConsumerDeviceItem[] = loc.consumerDevices ?? [];
        return {
          id: loc.id ?? '',
          name: loc.name ?? 'Unknown Location',
          fullAddress: parts.join(', '),
          devices: filterByFamily(allDevices, 'Camera'),
          thermostatDevices: filterByFamily(allDevices, 'Thermostat'),
          leakDetectorDevices: filterByFamily(allDevices, 'LeakDetector'),
          lockDevices: [],
          securityPanelDevices: [],
        };
      });
  }, [accountsData, assignedLocationIds, consumerAccountId]);

  // ── Selection state — all start empty (user must explicitly select) ────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [cameraSelections, setCameraSelections] = useState<Map<string, Set<string>>>(new Map());
  const [camerasOn, setCamerasOn] = useState<Set<string>>(new Set());

  const handleToggleLocation = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // ── Save state ─────────────────────────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false);

  // Save is valid when at least one location is selected and (for non-ADMIN)
  // at least one of those locations has a camera selected.
  const isValid =
    role === 'ADMIN'
      ? selectedIds.size > 0
      : selectedIds.size > 0 &&
        [...selectedIds].some(locId => (cameraSelections.get(locId)?.size ?? 0) > 0);

  // ── Escape key closes the overlay ─────────────────────────────────────────
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isSaving) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isSaving, onClose]);

  // ── Save handler ───────────────────────────────────────────────────────────
  async function handleSave() {
    setIsSaving(true);
    try {
      // Preserve all device IDs from the user's currently assigned locations.
      const existingDeviceIds = (detailData?.data?.consumerAccount?.locations ?? [])
        .flatMap(l => (l.consumerDevices ?? []).map(d => d.device?.deviceId ?? d.id))
        .filter((id): id is string => Boolean(id));

      // Device IDs from the newly selected locations.
      const newDeviceIds = [...selectedIds].flatMap(locId => [
        ...(cameraSelections.get(locId) ?? new Set<string>()),
      ]);

      const mergedIds = [...existingDeviceIds, ...newDeviceIds];

      const now = new Date();
      const expiresAt = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();

      const body: UpdateUserInviteDataModel = {
        invitedUserId: invitedUserId ?? undefined,
        inviteDataInfo: {
          role: role as InviteRole,
          sendToEmail: email,
          deviceIds: mergedIds,
          ...(role === 'GUEST' && {
            scheduleInfo: {
              weekDays: ALL_WEEK_DAYS,
              startDate: now.toISOString(),
              endDate: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(),
              startTime: '00:01',
              endTime: '23:59',
            },
          }),
        },
        emailTemplateInfo: {
          templateLanguage: 'en-US',
          baseUrl: `${import.meta.env.VITE_RESIDEO_ID_URL}/mobile`,
          expiresAt,
        },
      };

      await apiService.updateInvite(consumerAccountId, body);
      await queryClient.invalidateQueries({
        queryKey: ['consumerUserDetail', consumerAccountId, consumerUserId],
      });
      onClose();
    } catch {
      addToast({ message: t('common.failedToSave'), toastType: 'Error', zIndex: '9999' });
    } finally {
      setIsSaving(false);
    }
  }

  // ── Expose save + notify parent ────────────────────────────────────────────
  useImperativeHandle(ref, () => ({ save: handleSave }));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onSavingChange?.(isSaving); }, [isSaving]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onValidChange?.(isValid); }, [isValid]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const locationsError = accountsError ? 'Unable to load locations. Please try again.' : null;

  return (
    <div className={styles.scrollArea}>
      <div className={styles.content}>
        <StepLocation
          locations={availableLocations}
          isLoading={isLoadingLocations}
          error={locationsError}
          selectedIds={selectedIds}
          cameraSelections={cameraSelections}
          camerasOn={camerasOn}
          onToggleLocation={handleToggleLocation}
          onCameraSelectionsChange={setCameraSelections}
          onCamerasOnChange={setCamerasOn}
          emptyMessage="User already has access to all locations. No additional location access can be added."
        />
      </div>
    </div>
  );
  }
);
